import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RichTextEditor from './components/RichTextEditor';
import ImageUploader from './components/ImageUploader';
import {saveBlogPost,getAllBlogPosts,deleteBlogPost} from '../utils/blogStorage';
import {getAllAuthors} from '../utils/authorStorage';
import {getAdminConfig,saveAdminConfig} from '../utils/adminConfig';
import {getStorageInfo,forceSupabaseConnection} from '../utils/database';

const {FiPlus,FiEdit2,FiTrash2,FiEye,FiSave,FiX,FiCalendar,FiUser,FiTag,FiSettings,FiSearch,FiFilter,FiGrid,FiList,FiStar,FiClock,FiDatabase,FiWifi,FiWifiOff}=FiIcons;

const BlogManager=()=> {
  const [posts,setPosts]=useState([]);
  const [isEditing,setIsEditing]=useState(false);
  const [editingPost,setEditingPost]=useState(null);
  const [showConfig,setShowConfig]=useState(false);
  const [viewMode,setViewMode]=useState('grid');
  const [searchQuery,setSearchQuery]=useState('');
  const [filterStatus,setFilterStatus]=useState('all');
  const [isLoading,setIsLoading]=useState(true);
  const [isDeleting,setIsDeleting]=useState(null);
  const [availableAuthors,setAvailableAuthors]=useState([]);
  const [resetTrigger,setResetTrigger]=useState(0);
  const [isSaving,setIsSaving]=useState(false);
  const [storageInfo,setStorageInfo]=useState({isSupabaseConnected: false,localStorage: {}});
  const [showStorageWarning,setShowStorageWarning]=useState(false);

  const [config,setConfig]=useState({
    categories: [
      'AI in Software Development',
      'Prompt Engineering Best Practices',
      'Agile & AI',
      'Future Tech Trends',
      'Case Study Deep Dives'
    ],
    authors: [],
    statusOptions: [
      'Draft',
      'Published',
      'Featured',
      'Archived'
    ]
  });

  const [formData,setFormData]=useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    readTime: '5 min read',
    status: 'draft',
    featured: false,
    image: '',
    tags: '',
    seoTitle: '',
    seoDescription: '',
    slug: ''
  });

  useEffect(()=> {
    loadPosts();
    loadConfig();
    loadAuthors();
    checkStorageStatus();
  },[]);

  // Listen for new blog post creation events
  useEffect(()=> {
    const handleCreateNewBlogPost=()=> {
      setIsEditing(true);
      resetForm();
    };

    window.addEventListener('createNewBlogPost',handleCreateNewBlogPost);
    return ()=> window.removeEventListener('createNewBlogPost',handleCreateNewBlogPost);
  },[]);

  const checkStorageStatus=()=> {
    const info=getStorageInfo();
    setStorageInfo(info);
    
    if (!info.isSupabaseConnected && info.localStorage.isNearLimit) {
      setShowStorageWarning(true);
    }
  };

  const handleConnectSupabase=async ()=> {
    console.log('🔄 Attempting to connect to Supabase...');
    const connected=await forceSupabaseConnection();
    
    if (connected) {
      checkStorageStatus();
      setShowStorageWarning(false);
      alert('✅ Successfully connected to Supabase! Your data will now be saved to the cloud.');
      // Reload posts to get data from Supabase
      await loadPosts();
    } else {
      alert('❌ Failed to connect to Supabase. Please check your internet connection and try again.');
    }
  };

  const loadPosts=async ()=> {
    try {
      setIsLoading(true);
      console.log('🔄 Loading blog posts...');
      const allPosts=await getAllBlogPosts();
      console.log('📊 Loaded posts:',allPosts.length);
      setPosts(Array.isArray(allPosts) ? allPosts : []);
    } catch (error) {
      console.error('❌ Error loading posts:',error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAuthors=async ()=> {
    try {
      const authors=await getAllAuthors();
      const activeAuthors=authors.filter(author=> author.status==='active');
      setAvailableAuthors(activeAuthors);

      // Update config with current authors
      setConfig(prev=> ({
        ...prev,
        authors: activeAuthors.map(author=> author.name)
      }));
    } catch (error) {
      console.error('Error loading authors:',error);
      setAvailableAuthors([]);
    }
  };

  const loadConfig=()=> {
    try {
      const adminConfig=getAdminConfig();
      if (adminConfig && adminConfig.blog) {
        setConfig(prev=> ({
          ...prev,
          categories: adminConfig.blog.categories || prev.categories,
          statusOptions: adminConfig.blog.statusOptions || prev.statusOptions
        }));
      }
    } catch (error) {
      console.error('Error loading config:',error);
    }
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    
    if (isSaving) {
      console.log('⏳ Already saving, please wait...');
      return;
    }

    try {
      setIsSaving(true);
      console.log('💾 Starting blog post save process...');

      const postData={
        ...formData,
        id: editingPost?.id || Date.now().toString(),
        date: editingPost?.date || new Date().toLocaleDateString(),
        createdAt: editingPost?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: formData.tags.split(',').map(tag=> tag.trim()).filter(Boolean),
        slug: formData.slug || generateSlug(formData.title)
      };

      console.log('📝 Blog post data to save:',postData);

      const result=await saveBlogPost(postData);
      console.log('💾 Save result:',result);

      if (result.success) {
        console.log('✅ Blog post saved successfully');
        
        // Show appropriate success message
        if (result.warning) {
          alert(`⚠️ Post saved with limitations: ${result.warning}`);
        } else {
          alert(editingPost ? 'Blog post updated successfully!' : 'Blog post created successfully!');
        }
        
        // Reload the list after saving
        await loadPosts();
        
        // Reset form and exit editing mode
        resetForm();
        
        // Update storage status
        checkStorageStatus();
      } else {
        throw new Error(result.error || 'Failed to save blog post');
      }
    } catch (error) {
      console.error('❌ Error saving post:',error);
      
      if (error.message.includes('quota') || error.message.includes('Storage')) {
        alert(`💾 Storage Error: ${error.message}\n\nRecommendation: Connect to Supabase for unlimited storage.`);
        setShowStorageWarning(true);
      } else {
        alert('Failed to save post. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const generateSlug=(title)=> {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g,'')
      .replace(/\s+/g,'-')
      .replace(/-+/g,'-')
      .trim('-');
  };

  const resetForm=()=> {
    console.log('🔄 Resetting form and image uploader...');
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      author: availableAuthors.length > 0 ? availableAuthors[0].name : '',
      readTime: '5 min read',
      status: 'draft',
      featured: false,
      image: '',
      tags: '',
      seoTitle: '',
      seoDescription: '',
      slug: ''
    });
    setEditingPost(null);
    setIsEditing(false);

    // Trigger ImageUploader reset
    setResetTrigger(prev=> prev + 1);
  };

  const handleEdit=(post)=> {
    console.log('✏️ Editing blog post:',post);
    setEditingPost(post);
    setFormData({
      ...post,
      tags: post.tags ? post.tags.join(',') : ''
    });
    setIsEditing(true);

    // Trigger ImageUploader reset with new data
    setResetTrigger(prev=> prev + 1);
  };

  const handleDelete=async (id)=> {
    console.log('🗑️ Attempting to delete blog post with id:',id);
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        setIsDeleting(id);
        console.log('🔄 Calling deleteBlogPost...');
        const result=await deleteBlogPost(id);
        console.log('🗑️ Delete result:',result);

        if (result.success) {
          console.log('✅ Delete successful, reloading blog posts...');
          // Reload the list after successful deletion
          await loadPosts();
          alert('Blog post deleted successfully!');
        } else {
          throw new Error(result.error || 'Delete failed');
        }
      } catch (error) {
        console.error('❌ Error deleting blog post:',error);
        alert('Failed to delete blog post. Please try again.');
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const handleChange=(e)=> {
    const {name,value,type,checked}=e.target;
    setFormData({
      ...formData,
      [name]: type==='checkbox' ? checked : value
    });

    if (name==='title') {
      setFormData(prev=> ({
        ...prev,
        title: value,
        slug: generateSlug(value),
        seoTitle: value
      }));
    }
  };

  const handleContentChange=(content)=> {
    setFormData(prev=> ({
      ...prev,
      content
    }));
  };

  const handleImageUpload=(imageUrl)=> {
    console.log('🖼️ Blog Manager: Image uploaded/changed to:',imageUrl);
    setFormData(prev=> ({
      ...prev,
      image: imageUrl
    }));
  };

  const saveConfig=()=> {
    try {
      const adminConfig=getAdminConfig();
      adminConfig.blog=config;
      saveAdminConfig(adminConfig);
      setShowConfig(false);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:',error);
      alert('Failed to save configuration. Please try again.');
    }
  };

  const addConfigItem=(type,item)=> {
    if (item && !config[type].includes(item)) {
      setConfig(prev=> ({
        ...prev,
        [type]: [...prev[type],item]
      }));
    }
  };

  const removeConfigItem=(type,index)=> {
    setConfig(prev=> ({
      ...prev,
      [type]: prev[type].filter((_,i)=> i !==index)
    }));
  };

  const filteredPosts=posts.filter(post=> {
    const matchesSearch=post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus=filterStatus==='all' || post.status===filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Storage Warning Component
  const StorageWarning=()=> (
    <AnimatePresence>
      {showStorageWarning && (
        <motion.div
          initial={{opacity: 0,y: -20}}
          animate={{opacity: 1,y: 0}}
          exit={{opacity: 0,y: -20}}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiDatabase} className="h-5 w-5 text-yellow-600 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900 mb-2">Storage Limitation Warning</h4>
              <p className="text-yellow-800 text-sm mb-3">
                {!storageInfo.isSupabaseConnected 
                  ? `You're using localStorage fallback (${storageInfo.localStorage.sizeInMB}MB used). This has limited capacity and may cause data loss.`
                  : 'Storage is nearly full. Some features may be limited.'
                }
              </p>
              <div className="flex items-center space-x-3">
                {!storageInfo.isSupabaseConnected && (
                  <button
                    onClick={handleConnectSupabase}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <SafeIcon icon={FiWifi} className="h-4 w-4 inline mr-2" />
                    Connect to Supabase
                  </button>
                )}
                <button
                  onClick={()=> setShowStorageWarning(false)}
                  className="px-4 py-2 text-yellow-700 hover:text-yellow-900 text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Light Configuration View
  if (showConfig) {
    return (
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Blog Configuration</h2>
          <motion.button
            onClick={()=> setShowConfig(false)}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
            <span>Back</span>
          </motion.button>
        </div>

        <StorageWarning />

        <div className="bg-white border border-gray-200 rounded-2xl p-8 space-y-8 shadow-sm">
          {/* Categories */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiTag} className="h-5 w-5 text-blue-600" />
              <span>Categories</span>
            </h3>
            <div className="space-y-4">
              {config.categories.map((category,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <span className="text-gray-900 font-medium">{category}</span>
                  <motion.button
                    onClick={()=> removeConfigItem('categories',index)}
                    whileHover={{scale: 1.1}}
                    whileTap={{scale: 0.9}}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-300"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </motion.div>
              ))}
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Add new category"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  onKeyPress={(e)=> {
                    if (e.key==='Enter') {
                      addConfigItem('categories',e.target.value);
                      e.target.value='';
                    }
                  }}
                />
                <motion.button
                  onClick={(e)=> {
                    const input=e.target.previousElementSibling;
                    addConfigItem('categories',input.value);
                    input.value='';
                  }}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Add
                </motion.button>
              </div>
            </div>
          </div>

          {/* Authors Info */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiUser} className="h-5 w-5 text-green-600" />
              <span>Authors</span>
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiUser} className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Dynamic Author Loading</h4>
                  <p className="text-blue-800 text-sm mb-3">
                    Authors are automatically loaded from the Author Manager. Currently available authors:
                  </p>
                  <div className="space-y-2">
                    {availableAuthors.length > 0 ? (
                      availableAuthors.map((author,index)=> (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="font-medium text-blue-900">{author.name}</span>
                          <span className="text-blue-700">({author.title})</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-blue-700 text-sm">No authors found. Create authors in the Author Manager first.</p>
                    )}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={()=> {
                        loadAuthors();
                        alert('Authors refreshed from database!');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Refresh Authors
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Options */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiSettings} className="h-5 w-5 text-purple-600" />
              <span>Status Options</span>
            </h3>
            <div className="space-y-4">
              {config.statusOptions.map((status,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <span className="text-gray-900 font-medium">{status}</span>
                  <motion.button
                    onClick={()=> removeConfigItem('statusOptions',index)}
                    whileHover={{scale: 1.1}}
                    whileTap={{scale: 0.9}}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-300"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </motion.div>
              ))}
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Add new status option"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  onKeyPress={(e)=> {
                    if (e.key==='Enter') {
                      addConfigItem('statusOptions',e.target.value);
                      e.target.value='';
                    }
                  }}
                />
                <motion.button
                  onClick={(e)=> {
                    const input=e.target.previousElementSibling;
                    addConfigItem('statusOptions',input.value);
                    input.value='';
                  }}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Add
                </motion.button>
              </div>
            </div>
          </div>

          <motion.button
            onClick={saveConfig}
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300"
          >
            Save Configuration
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // Light Editing Form View
  if (isEditing) {
    return (
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {editingPost ? 'Edit Post' : 'New Post'}
          </h2>
          <motion.button
            onClick={resetForm}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
            <span>Cancel</span>
          </motion.button>
        </div>

        <StorageWarning />

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 space-y-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Title *</label>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                  placeholder="Enter post title"
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Excerpt *</label>
                <textarea
                  name="excerpt"
                  required
                  rows={3}
                  value={formData.excerpt}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50"
                  placeholder="Brief description of the post"
                />
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Content *</label>
                <RichTextEditor
                  value={formData.content}
                  onChange={handleContentChange}
                  placeholder="Write your blog post content here..."
                />
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Featured Image</label>
                <ImageUploader
                  onUpload={handleImageUpload}
                  currentImage={formData.image}
                  label="Upload featured image"
                  resetTrigger={resetTrigger}
                />
                {!storageInfo.isSupabaseConnected && (
                  <p className="text-xs text-yellow-600 mt-2">
                    ⚠️ Large images may cause storage issues. Connect to Supabase for better image handling.
                  </p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Database Status */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <SafeIcon 
                    icon={storageInfo.isSupabaseConnected ? FiWifi : FiWifiOff} 
                    className={`h-5 w-5 ${storageInfo.isSupabaseConnected ? 'text-green-600' : 'text-yellow-600'}`} 
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {storageInfo.isSupabaseConnected ? 'Cloud Storage' : 'Local Storage'}
                  </span>
                </div>
                {!storageInfo.isSupabaseConnected && (
                  <button
                    type="button"
                    onClick={handleConnectSupabase}
                    className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Connect to Cloud
                  </button>
                )}
              </div>

              {/* Category */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Category *</label>
                <select
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                >
                  <option value="">Select category</option>
                  {config.categories.map(category=> (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Author */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Author *</label>
                {availableAuthors.length > 0 ? (
                  <select
                    name="author"
                    required
                    value={formData.author}
                    onChange={handleChange}
                    disabled={isSaving}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                  >
                    <option value="">Select author</option>
                    {availableAuthors.map(author=> (
                      <option key={author.id} value={author.name}>
                        {author.name} - {author.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <p className="text-yellow-800 text-sm mb-2">No authors available</p>
                    <p className="text-yellow-700 text-xs">Create authors in the Author Manager first, then refresh this page.</p>
                    <button
                      type="button"
                      onClick={()=> {
                        loadAuthors();
                        alert('Authors refreshed!');
                      }}
                      className="mt-2 px-3 py-1 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700 transition-colors"
                    >
                      Refresh Authors
                    </button>
                  </div>
                )}
              </div>

              {/* Status & Featured */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={isSaving}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                  >
                    {config.statusOptions.map(status=> (
                      <option key={status} value={status.toLowerCase()}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    disabled={isSaving}
                    className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                  />
                  <label htmlFor="featured" className="text-sm text-gray-700 flex items-center space-x-2">
                    <SafeIcon icon={FiStar} className="h-4 w-4 text-yellow-500" />
                    <span>Featured Article</span>
                  </label>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Tags</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:opacity-50"
                  placeholder="tag1, tag2, tag3"
                />
                <p className="text-xs text-gray-500 mt-2">Separate tags with commas</p>
              </div>

              {/* Save Button */}
              <motion.button
                type="submit"
                disabled={isSaving}
                whileHover={!isSaving ? {scale: 1.02} : {}}
                whileTap={!isSaving ? {scale: 0.98} : {}}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="h-5 w-5" />
                    <span>{editingPost ? 'Update Post' : 'Save Post'}</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </form>
      </motion.div>
    );
  }

  // Main Blog Manager View with Light Theme
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Blog Manager</h2>
          <p className="text-gray-600">Manage your blog posts and content</p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            onClick={()=> setShowConfig(true)}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiSettings} className="h-5 w-5" />
            <span>Configure</span>
          </motion.button>
          <motion.button
            onClick={()=> {
              // Refresh authors before creating new post
              loadAuthors();
              setIsEditing(true);
            }}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            <span>New Post</span>
          </motion.button>
        </div>
      </div>

      <StorageWarning />

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e)=> setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e)=> setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="featured">Featured</option>
            </select>
          </div>
          <div className="flex items-center space-x-2 bg-gray-100 rounded-xl p-1 border border-gray-200">
            <motion.button
              onClick={()=> setViewMode('grid')}
              whileTap={{scale: 0.95}}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode==='grid' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiGrid} className="h-4 w-4" />
            </motion.button>
            <motion.button
              onClick={()=> setViewMode('list')}
              whileTap={{scale: 0.95}}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode==='list' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiList} className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Posts Display */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">All Posts ({filteredPosts.length})</h3>
          <div className="flex items-center space-x-2 text-sm">
            <SafeIcon 
              icon={storageInfo.isSupabaseConnected ? FiWifi : FiWifiOff} 
              className={`h-4 w-4 ${storageInfo.isSupabaseConnected ? 'text-green-600' : 'text-yellow-600'}`} 
            />
            <span className={storageInfo.isSupabaseConnected ? 'text-green-600' : 'text-yellow-600'}>
              {storageInfo.isSupabaseConnected ? 'Cloud' : 'Local'}
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading posts...</p>
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className={viewMode==='grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6' : 'divide-y divide-gray-100'}>
            {filteredPosts.map((post,index)=> (
              <motion.div
                key={post.id}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: index * 0.1}}
                className={viewMode==='grid' 
                  ? 'bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 group' 
                  : 'p-6 hover:bg-gray-50 transition-all duration-300'
                }
              >
                {viewMode==='grid' ? (
                  <>
                    {/* Grid Card View */}
                    {post.image && (
                      <div className="aspect-video bg-gray-100 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        {post.featured && (
                          <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200">
                            <SafeIcon icon={FiStar} className="h-3 w-3" />
                            <span>Featured</span>
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                          post.status==='published' 
                            ? 'bg-green-100 text-green-700 border-green-200' 
                            : post.status==='draft' 
                            ? 'bg-gray-100 text-gray-700 border-gray-200' 
                            : 'bg-blue-100 text-blue-700 border-blue-200'
                        }`}>
                          {post.status}
                        </span>
                        {post._minimal && (
                          <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded-full border border-red-200" title="Minimal data due to storage constraints">
                            Limited
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{post.title}</h4>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUser} className="h-3 w-3" />
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} className="h-3 w-3" />
                            <span>{post.date}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="h-3 w-3" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full border border-blue-200">
                          {post.category}
                        </span>
                        <div className="flex items-center space-x-2">
                          <motion.button
                            onClick={()=> handleEdit(post)}
                            whileHover={{scale: 1.1}}
                            whileTap={{scale: 0.9}}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-300"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            onClick={()=> handleDelete(post.id)}
                            disabled={isDeleting===post.id}
                            whileHover={{scale: 1.1}}
                            whileTap={{scale: 0.9}}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300 disabled:opacity-50"
                            title="Delete"
                          >
                            {isDeleting===post.id ? (
                              <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                            )}
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* List View */
                  <div className="flex items-start justify-between">
                    <div className="flex space-x-4 flex-1">
                      {post.image && (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-20 h-20 object-cover rounded-xl border border-gray-200"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{post.title}</h4>
                          {post.featured && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200">
                              <SafeIcon icon={FiStar} className="h-3 w-3" />
                              <span>Featured</span>
                            </span>
                          )}
                          <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                            post.status==='published' 
                              ? 'bg-green-100 text-green-700 border-green-200' 
                              : post.status==='draft' 
                              ? 'bg-gray-100 text-gray-700 border-gray-200' 
                              : 'bg-blue-100 text-blue-700 border-blue-200'
                          }`}>
                            {post.status}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{post.excerpt}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUser} className="h-4 w-4" />
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} className="h-4 w-4" />
                            <span>{post.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiTag} className="h-4 w-4" />
                            <span>{post.category}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <motion.button
                        onClick={()=> handleEdit(post)}
                        whileHover={{scale: 1.1}}
                        whileTap={{scale: 0.9}}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-300"
                        title="Edit"
                      >
                        <SafeIcon icon={FiEdit2} className="h-5 w-5" />
                      </motion.button>
                      <motion.button
                        onClick={()=> handleDelete(post.id)}
                        disabled={isDeleting===post.id}
                        whileHover={{scale: 1.1}}
                        whileTap={{scale: 0.9}}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300 disabled:opacity-50"
                        title="Delete"
                      >
                        {isDeleting===post.id ? (
                          <div className="h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                        )}
                      </motion.button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <SafeIcon icon={FiEdit2} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No blog posts yet</p>
            <p className="text-gray-500 mb-6">Create your first post to get started</p>
            <motion.button
              onClick={()=> setIsEditing(true)}
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
            >
              Create First Post
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManager;